import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Inject } from '@angular/core';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { v4 as uuidV4 } from 'uuid';
import { CbreHttpConfig, CbreHttpConfigToken } from '../cbre-http.config';
import { ErrorFactory } from '../errors/error-factory.service';
import { X_CBRE_REQUEST_ID_HEADER } from '../utils/constants';
import { mergeHttpHeaders } from '../utils/merge-headers.service';

/**
 * Intercepts all http request and adds stats (when request start and when it finishes) and
 * binds transactional and span-ing identifiers.
 * In case of any error it captures the error headers and adds captured stats to HttpErrorResponse
 * logs it. if Debug is enabled it also debugs the stats information to configured transporters
 *
 * @example
 * // given requestIdHeaderName = 'x-request-id', the following headers will bind to the request
 * {
 *   'x-request-id': "653016f4-5a94-5545-bacd-1cca6b868215",
 *   'x-request-id-StartTs': "1603763764800",
 *   'x-request-id-EndTs': "1603763764900"
 * }
 */
export class HttpSpanInterceptorService implements HttpInterceptor {
  private traceHeaderName: string;

  constructor(
    @Inject(CbreHttpConfigToken) private config: CbreHttpConfig,
    @Inject(CbreLoggerToken) private logger: CbreLogger
  ) {
    const { requestIdHeaderName = X_CBRE_REQUEST_ID_HEADER } = config;
    this.traceHeaderName = requestIdHeaderName;
  }
  /**
   * Append CBRE_X_TRANSACTION_ID_TOKEN header to the request and ensure headers are passed
   * @param request
   * @param next
   */
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    /*
     * Ignore any configured skip url interception, at least LOGGING server should be provided
     * if logger implements HttpTransporter else can fall in a infinite loop because it will
     * intercept also the http transport
     * */
    if (this.config.skipUrlInterception.some(urlPattern => request.url.includes(urlPattern))) {
      // skip logging calls or any other provided url
      return next.handle(request);
    }
    const timeZoneOffset = ((new Date().getTimezoneOffset() * -1) / 60).toPrecision(2).toString();
    // creates the uuid
    const requestIDStartTs = Date.now();
    const requestID = uuidV4();
    // attach uuid and stats metadata to request headers
    const nextRequest = request.clone({
      headers: request.headers
        .set(`${this.traceHeaderName}-StartTs`, requestIDStartTs.toString())
        .set(this.traceHeaderName, requestID)
        .set('time-zone', timeZoneOffset),
    });
    const headers = nextRequest.headers;
    // release the request
    return next.handle(nextRequest).pipe(
      tap(() =>
        this.logger.debug({
          eventName: 'request finished',
          requestID,
          body: request.body,
          requestUrl: request.url,
          requestIDStartTs,
          requestIDEndTs: Date.now(),
        })
      ),
      catchError((error: HttpErrorResponse) => {
        // re-throw the error ensuring headers are being passed
        const nextHeaders = mergeHttpHeaders(
          headers,
          error.headers,
          new HttpHeaders({ [`${this.traceHeaderName}-EndTs`]: Date.now().toString() })
        );
        const categorizedError = ErrorFactory.GetError({ ...error, headers: nextHeaders });
        return throwError(() => categorizedError);
      })
    );
  }
}
